/**
 * FailedPostsDrawer — Phase 5C.2D
 *
 * Shared component for Calendar + Library pages.
 * Shows an amber "⚠ N Failed" badge in the page header.
 * Clicking opens a right-side drawer listing failed / dead_letter posts
 * with per-row Retry and Cancel actions.
 *
 * options.getBadgeAnchor — () => DOM element to insert badge into
 * options.toast          — (type, message) => void
 * options.confirm        — async (opts) => boolean
 * options.onRetry        — async (postId) => void   (optional)
 * options.onCancel       — async (postId) => void   (optional)
 */
import { client } from '../api/client.js';

export class FailedPostsDrawer {
    constructor(options = {}) {
        this.getBadgeAnchor = options.getBadgeAnchor;
        this.toast = options.toast || (() => {});
        this.confirm = options.confirm || (async () => false);
        this.onRetry = options.onRetry || (async () => {});
        this.onCancel = options.onCancel || (async () => {});

        this.items = [];
        this.badgeEl = null;
        this.drawerEl = null;
        this.overlayEl = null;
        this._isOpen = false;
        this._boundKeydown = this._onKeydown.bind(this);
    }

    // ─── Public API ──────────────────────────────────────────────

    async init() {
        await this._fetchQueue();
        this._ensureBadge();
        this._updateBadge();
    }

    openDrawer() {
        this._ensureBadge();
        this._ensureDrawer();
        this._renderRows();
        this.drawerEl.classList.add('fp-drawer--open');
        this.overlayEl.classList.add('fp-drawer__overlay--active');
        this._isOpen = true;
        if (this.badgeEl) this.badgeEl.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', this._boundKeydown);
        // Focus the close button for accessibility
        const closeBtn = this.drawerEl.querySelector('.fp-drawer__close');
        if (closeBtn) requestAnimationFrame(() => closeBtn.focus());
    }

    closeDrawer() {
        if (!this._isOpen) return;
        if (this.drawerEl) this.drawerEl.classList.remove('fp-drawer--open');
        if (this.overlayEl) this.overlayEl.classList.remove('fp-drawer__overlay--active');
        this._isOpen = false;
        if (this.badgeEl) this.badgeEl.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', this._boundKeydown);
        // Return focus to badge
        if (this.badgeEl && document.body.contains(this.badgeEl)) {
            this.badgeEl.focus();
        }
    }

    async refresh() {
        await this._fetchQueue();
        this._ensureBadge();
        this._updateBadge();
        if (this._isOpen) {
            this._renderRows();
        }
        if (this.items.length === 0) {
            this.closeDrawer();
        }
    }

    destroy() {
        this.closeDrawer();
        if (this.badgeEl && this.badgeEl.parentNode) {
            this.badgeEl.parentNode.removeChild(this.badgeEl);
        }
        if (this.drawerEl && this.drawerEl.parentNode) {
            this.drawerEl.parentNode.removeChild(this.drawerEl);
        }
        if (this.overlayEl && this.overlayEl.parentNode) {
            this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
        document.removeEventListener('keydown', this._boundKeydown);
        this.badgeEl = null;
        this.drawerEl = null;
        this.overlayEl = null;
    }

    // ─── Internal: Data ──────────────────────────────────────────

    async _fetchQueue() {
        try {
            const res = await client.getPublishQueue();
            this.items = (res && res.items) ? res.items : [];
        } catch (err) {
            console.error('[FailedPostsDrawer] queue fetch error:', err);
            this.items = [];
        }
    }

    // ─── Internal: Badge ─────────────────────────────────────────

    _ensureBadge() {
        // If badge is detached from DOM (library re-render), recreate it
        if (this.badgeEl && !document.body.contains(this.badgeEl)) {
            this.badgeEl = null;
        }
        if (!this.badgeEl) {
            this.badgeEl = document.createElement('button');
            this.badgeEl.className = 'failed-badge-btn failed-badge-btn--hidden';
            this.badgeEl.setAttribute('role', 'button');
            this.badgeEl.setAttribute('aria-haspopup', 'true');
            this.badgeEl.setAttribute('aria-expanded', 'false');
            this.badgeEl.onclick = () => {
                if (this._isOpen) {
                    this.closeDrawer();
                } else {
                    this.openDrawer();
                }
            };
        }
        const anchor = this.getBadgeAnchor ? this.getBadgeAnchor() : null;
        if (anchor && !anchor.contains(this.badgeEl)) {
            anchor.insertBefore(this.badgeEl, anchor.firstChild);
        }
    }

    _updateBadge() {
        if (!this.badgeEl) return;
        const count = this.items.length;
        if (count === 0) {
            this.badgeEl.classList.add('failed-badge-btn--hidden');
            this.badgeEl.setAttribute('aria-label', 'No failed posts');
        } else {
            this.badgeEl.classList.remove('failed-badge-btn--hidden');
            this.badgeEl.textContent = `⚠ ${count} Failed`;
            this.badgeEl.setAttribute('aria-label', `View ${count} failed post${count > 1 ? 's' : ''}`);
        }
    }

    // ─── Internal: Drawer DOM ────────────────────────────────────

    _ensureDrawer() {
        if (this.drawerEl && document.body.contains(this.drawerEl)) return;

        // Overlay
        this.overlayEl = document.createElement('div');
        this.overlayEl.className = 'fp-drawer__overlay';
        this.overlayEl.onclick = () => this.closeDrawer();
        document.body.appendChild(this.overlayEl);

        // Drawer panel
        this.drawerEl = document.createElement('div');
        this.drawerEl.className = 'fp-drawer';
        this.drawerEl.setAttribute('role', 'dialog');
        this.drawerEl.setAttribute('aria-label', 'Failed Posts');
        this.drawerEl.innerHTML = `
            <div class="fp-drawer__header">
                <div class="fp-drawer__header-text">
                    <h3 class="fp-drawer__title">Failed Posts</h3>
                    <p class="fp-drawer__subtitle"></p>
                </div>
                <button class="fp-drawer__close icon-btn" aria-label="Close" title="Close"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div class="fp-drawer__body"></div>
            <div class="fp-drawer__footer">
                <button class="fp-drawer__refresh-btn btn-secondary btn-sm">Refresh</button>
                <button class="fp-drawer__close-btn btn-secondary btn-sm">Close</button>
            </div>
        `;
        document.body.appendChild(this.drawerEl);

        // Wire drawer header/footer buttons
        this.drawerEl.querySelector('.fp-drawer__close').onclick = () => this.closeDrawer();
        this.drawerEl.querySelector('.fp-drawer__close-btn').onclick = () => this.closeDrawer();
        this.drawerEl.querySelector('.fp-drawer__refresh-btn').onclick = async () => {
            await this.refresh();
        };
    }

    // ─── Internal: Row rendering ─────────────────────────────────

    _renderRows() {
        if (!this.drawerEl) return;
        const body = this.drawerEl.querySelector('.fp-drawer__body');
        const subtitle = this.drawerEl.querySelector('.fp-drawer__subtitle');
        if (!body) return;

        const count = this.items.length;
        if (subtitle) {
            subtitle.textContent = count > 0
                ? `${count} post${count > 1 ? 's' : ''} failed to publish`
                : '';
        }

        if (count === 0) {
            body.innerHTML = `
                <div class="fp-empty">
                    <span class="fp-empty__icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg></span>
                    <p class="fp-empty__text">All clear — no failed posts.</p>
                </div>`;
            return;
        }

        body.innerHTML = this.items.map(item => `
            <div class="fp-row" data-post-id="${this._esc(item.id)}">
                <div class="fp-row__top">
                    <span class="fp-row__platform">${this._esc((item.platform || '').toUpperCase())}</span>
                    <span class="fp-row__status fp-row__status--${this._esc(item.status)}">${item.status === 'dead_letter' ? 'Needs Attention' : 'Failed'}</span>
                </div>
                <p class="fp-row__snippet">${this._esc(this._truncate(item.content, 80))}</p>
                <div class="fp-row__meta">
                    <span class="fp-row__date">Scheduled: ${this._formatDate(item.scheduled_for)}</span>
                    <span class="fp-row__attempts">Attempt ${item.retry_count || 0} of ${item.max_retries || 3}</span>
                </div>
                <p class="fp-row__error" title="${this._esc(item.last_error || '')}">${this._esc(this._truncate(item.last_error || 'Unknown error', 120))}</p>
                <div class="fp-row__actions">
                    <button class="fp-row__retry btn-primary btn-sm" data-post-id="${this._esc(item.id)}">Retry</button>
                    <button class="fp-row__cancel btn-secondary btn-sm" data-post-id="${this._esc(item.id)}">Cancel</button>
                </div>
            </div>
        `).join('');

        // Wire action buttons
        body.querySelectorAll('.fp-row__retry').forEach(btn => {
            btn.onclick = () => this._handleRetry(btn.dataset.postId);
        });
        body.querySelectorAll('.fp-row__cancel').forEach(btn => {
            btn.onclick = () => this._handleCancel(btn.dataset.postId);
        });
    }

    // ─── Internal: Actions ───────────────────────────────────────

    async _handleRetry(postId) {
        const row = this.drawerEl?.querySelector(`.fp-row[data-post-id="${postId}"]`);
        const retryBtn = row?.querySelector('.fp-row__retry');
        const cancelBtn = row?.querySelector('.fp-row__cancel');

        // Disable buttons
        if (retryBtn) { retryBtn.disabled = true; retryBtn.textContent = 'Retrying…'; }
        if (cancelBtn) cancelBtn.disabled = true;

        try {
            await client.retryPublish(postId);
            await this.onRetry(postId);
            this.toast('success', 'Post queued for retry.');
            await this.refresh();
        } catch (err) {
            // Re-enable buttons
            if (retryBtn) { retryBtn.disabled = false; retryBtn.textContent = 'Retry'; }
            if (cancelBtn) cancelBtn.disabled = false;
            this.toast('error', `Retry failed: ${err.message || 'Unknown error'}`);
        }
    }

    async _handleCancel(postId) {
        const confirmed = await this.confirm({
            title: 'Cancel Post',
            message: 'Stop publishing this post? It will be marked cancelled.'
        });
        if (!confirmed) return;

        const row = this.drawerEl?.querySelector(`.fp-row[data-post-id="${postId}"]`);
        const retryBtn = row?.querySelector('.fp-row__retry');
        const cancelBtn = row?.querySelector('.fp-row__cancel');

        if (retryBtn) retryBtn.disabled = true;
        if (cancelBtn) { cancelBtn.disabled = true; cancelBtn.textContent = 'Cancelling…'; }

        try {
            await client.cancelPublish(postId);
            await this.onCancel(postId);
            this.toast('success', 'Post cancelled.');
            await this.refresh();
        } catch (err) {
            if (retryBtn) retryBtn.disabled = false;
            if (cancelBtn) { cancelBtn.disabled = false; cancelBtn.textContent = 'Cancel'; }
            this.toast('error', `Cancel failed: ${err.message || 'Unknown error'}`);
        }
    }

    // ─── Internal: Utilities ─────────────────────────────────────

    _onKeydown(e) {
        if (e.key === 'Escape' && this._isOpen) {
            e.stopPropagation();
            this.closeDrawer();
        }
    }

    _esc(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = String(str);
        return div.innerHTML;
    }

    _truncate(str, max) {
        if (!str) return '';
        return str.length > max ? str.slice(0, max) + '…' : str;
    }

    _formatDate(dateStr) {
        if (!dateStr) return '—';
        try {
            const d = new Date(dateStr);
            return d.toLocaleString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    }
}
